import React from 'react';
import { Link } from 'react-router-dom';
import {
  AppleIcon,
  SearchIcon,
  LeafIcon,
  UtensilsIcon,
  BuildingIcon,
  TypeIcon,
  QrCodeIcon,
  TargetIcon,
  VolumeIcon,
  VibrateIcon,
  GlobeIcon,
  BarChartIcon,
  LinkIcon,
  MicIcon,
  CloudIcon,
  MessageIcon,
  ShieldIcon,
  FileTextIcon,
  CameraIcon,
  SmartphoneIcon,
  EyeIcon,
  ZapIcon,
} from '../components/Icons';

const IOS_LINK = 'https://apps.apple.com/us/app/ai-vision-objects-identifier/id6752822635';

const MODES = [
  { icon: SearchIcon, label: 'Objects', desc: 'Everyday items, electronics, furniture, tools' },
  { icon: LeafIcon, label: 'Plants', desc: 'Flowers, trees, houseplants with care tips' },
  { icon: UtensilsIcon, label: 'Food', desc: 'Dishes, ingredients, nutrition estimates' },
  { icon: BuildingIcon, label: 'Landmarks', desc: 'Buildings, monuments, tourist attractions' },
  { icon: TypeIcon, label: 'Text (OCR)', desc: 'Read printed or handwritten text' },
  { icon: QrCodeIcon, label: 'Barcodes & QR', desc: 'Scan codes, product lookup' },
];

const STEPS = [
  {
    num: '01',
    icon: CameraIcon,
    title: 'Point your camera',
    desc: 'Open AI Vision and aim at any object. Detection starts automatically — no button needed.',
  },
  {
    num: '02',
    icon: EyeIcon,
    title: 'See AR labels appear',
    desc: 'Floating labels identify each object in real-time with confidence scores.',
  },
  {
    num: '03',
    icon: ZapIcon,
    title: 'Tap to explore',
    desc: 'Tap any label for details, then search, share, speak, translate, or save.',
  },
];

const FEATURES = [
  { icon: TargetIcon, title: 'AR Labels', desc: 'Floating labels overlay on each detected object in real-time' },
  { icon: VolumeIcon, title: 'Speak Results', desc: 'Text-to-speech reads out detections for hands-free use' },
  { icon: VibrateIcon, title: 'Haptic Feedback', desc: 'Feel a subtle vibration when new objects are detected' },
  { icon: GlobeIcon, title: 'Translate', desc: 'Translate object names into 50+ languages instantly' },
  { icon: BarChartIcon, title: 'History & Export', desc: 'Auto-saved detection history with PDF and CSV export' },
  { icon: LinkIcon, title: 'Quick Actions', desc: 'Search web, share, save to favorites, or find similar items' },
  { icon: MicIcon, title: 'Voice Commands', desc: 'Say "capture", "switch camera", or "toggle flash"' },
  { icon: CloudIcon, title: 'Cloud Backup', desc: 'Premium users get automatic cloud image backup' },
];

const COMPARE = [
  ['Daily scans', '5', 'Unlimited'],
  ['Objects per scan', '3', '5'],
  ['Detection speed', '2 seconds', '1 second'],
  ['Detection modes', '6', '6'],
  ['AR labels & actions', 'Yes', 'Yes'],
  ['History & favorites', 'Yes', 'Yes'],
  ['Fun facts & pricing', '—', 'Yes'],
  ['Cloud backup', '—', 'Yes'],
  ['Export (PDF / CSV)', '—', 'Yes'],
  ['Voice commands', '—', 'Yes'],
  ['Ad-free', '—', 'Yes'],
];

const Home = () => {
  return (
    <div>
      {/* ── Hero ── */}
      <section className="hero">
        <div className="container">
          <span className="section-label">AI-Powered Object Recognition</span>
          <h1>See the world through<br />the eyes of AI</h1>
          <p className="hero-subtitle">
            Point your camera at anything — objects, plants, food, landmarks, text, or barcodes.
            AI Vision identifies it instantly with real-time AR labels.
          </p>

          <div className="hero-buttons">
            <a href={IOS_LINK} target="_blank" rel="noopener noreferrer" className="btn btn-app-store">
              <AppleIcon size={26} />
              <span className="btn-app-store-text">
                <small>Download on the</small>
                <span>App Store</span>
              </span>
            </a>
            <a href="#features" className="btn btn-outline">
              <EyeIcon size={18} />
              See Features
            </a>
          </div>

          <div className="hero-stats">
            <div className="hero-stat">
              <span className="hero-stat-value">6</span>
              <span className="hero-stat-label">Detection Modes</span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-value">Real-time</span>
              <span className="hero-stat-label">AR Detection</span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-value">Gemini AI</span>
              <span className="hero-stat-label">Powered By</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Detection Modes ── */}
      <section className="section section-alt" id="features">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Detection Modes</span>
            <h2>Identify anything around you</h2>
            <p>Six specialized AI modes, each optimized for its category.</p>
          </div>

          <div className="mode-grid">
            {MODES.map(({ icon: ModeIcon, label, desc }) => (
              <div className="mode-item" key={label}>
                <span className="mode-icon"><ModeIcon size={32} style={{ color: '#8B5CF6' }} /></span>
                <span className="mode-label">{label}</span>
                <p style={{ fontSize: '0.82rem', marginTop: '8px', marginBottom: 0, lineHeight: 1.5 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">How It Works</span>
            <h2>Three steps, zero effort</h2>
            <p>No buttons to press. Just open the app, point your camera, and AI does the rest.</p>
          </div>

          <div className="grid grid-3">
            {STEPS.map(({ num, icon: StepIcon, title, desc }) => (
              <div className="card feature-card" key={num}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px',
                }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: 'rgba(108, 58, 237, 0.12)',
                    border: '1px solid rgba(108, 58, 237, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <StepIcon size={20} style={{ color: '#8B5CF6' }} />
                  </div>
                  <span style={{
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: 'rgba(139, 92, 246, 0.4)',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                  }}>
                    Step {num}
                  </span>
                </div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Key Features ── */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Features</span>
            <h2>Packed with powerful features</h2>
          </div>

          <div className="grid grid-4">
            {FEATURES.map(({ icon: FeatIcon, title, desc }) => (
              <div className="card feature-card" key={title}>
                <div className="card-icon">
                  <FeatIcon size={22} style={{ color: '#8B5CF6' }} />
                </div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="section" id="pricing">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Pricing</span>
            <h2>Start free, upgrade anytime</h2>
            <p>Try all core features for free. Go premium for unlimited power.</p>
          </div>

          <div className="pricing-grid">
            {/* Free */}
            <div className="pricing-card">
              <div style={{ marginBottom: '4px' }}>
                <SmartphoneIcon size={28} style={{ color: 'var(--text-muted)' }} />
              </div>
              <h3 style={{ marginTop: '8px', fontSize: '1.1rem', color: 'var(--text-muted)' }}>Free</h3>
              <div className="pricing-price">$0</div>
              <div className="pricing-period">Forever</div>
              <ul className="pricing-features">
                <li className="pricing-check">5 scans per day</li>
                <li className="pricing-check">3 objects per scan</li>
                <li className="pricing-check">6 detection modes</li>
                <li className="pricing-check">AR labels & actions</li>
                <li className="pricing-check">Detection history</li>
                <li>Ad-supported</li>
              </ul>
              <a
                href={IOS_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline"
                style={{ width: '100%', justifyContent: 'center' }}
              >
                Get Started
              </a>
            </div>

            {/* Premium */}
            <div className="pricing-card featured">
              <span className="pricing-badge">Best Value</span>
              <div style={{ marginBottom: '4px' }}>
                <ZapIcon size={28} style={{ color: 'var(--primary-light)' }} />
              </div>
              <h3 style={{ marginTop: '8px', fontSize: '1.1rem', color: 'var(--primary-light)' }}>Premium</h3>
              <div className="pricing-price">$3.99</div>
              <div className="pricing-period">per month &middot; or $29.99/year (save 37%)</div>
              <ul className="pricing-features">
                <li className="pricing-check">Unlimited scans</li>
                <li className="pricing-check">5 objects per scan</li>
                <li className="pricing-check">Faster 1s detection</li>
                <li className="pricing-check">Fun facts & price estimates</li>
                <li className="pricing-check">Cloud image backup</li>
                <li className="pricing-check">Data export (PDF, CSV)</li>
                <li className="pricing-check">Voice commands</li>
                <li className="pricing-check">Ad-free experience</li>
              </ul>
              <a
                href={IOS_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
              >
                Start Free Trial
              </a>
            </div>
          </div>

          {/* Comparison table */}
          <div style={{ maxWidth: '720px', margin: '0 auto', overflowX: 'auto' }}>
            <table className="compare-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free</th>
                  <th>Premium</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map(([feature, free, premium]) => (
                  <tr key={feature}>
                    <td>{feature}</td>
                    <td className={free === 'Yes' ? 'check' : free === '—' ? 'cross' : ''}>{free}</td>
                    <td className={premium === 'Yes' ? 'check' : ''}>{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section section-alt">
        <div className="container">
          <h2 style={{ marginBottom: '16px' }}>Ready to see the world differently?</h2>
          <p style={{ fontSize: '1.15rem', maxWidth: '480px', margin: '0 auto 32px' }}>
            Download AI Vision for free and start identifying objects, plants, food, and more — in seconds.
          </p>
          <a href={IOS_LINK} target="_blank" rel="noopener noreferrer" className="btn btn-app-store" style={{ fontSize: '1.05rem' }}>
            <AppleIcon size={28} />
            <span className="btn-app-store-text">
              <small>Download on the</small>
              <span>App Store</span>
            </span>
          </a>
        </div>
      </section>

      {/* ── Quick Links ── */}
      <section className="section">
        <div className="container">
          <div className="grid grid-3">
            <div className="card" style={{ textAlign: 'center' }}>
              <div className="card-icon" style={{ margin: '0 auto 16px' }}>
                <MessageIcon size={22} style={{ color: '#8B5CF6' }} />
              </div>
              <h3 style={{ marginTop: 0 }}>Support Center</h3>
              <p>FAQs, troubleshooting, and how to get in touch with us.</p>
              <Link to="/support" className="btn btn-outline" style={{ marginTop: '8px' }}>
                Get Help
              </Link>
            </div>

            <div className="card" style={{ textAlign: 'center' }}>
              <div className="card-icon" style={{ margin: '0 auto 16px' }}>
                <ShieldIcon size={22} style={{ color: '#8B5CF6' }} />
              </div>
              <h3 style={{ marginTop: 0 }}>Privacy Policy</h3>
              <p>How we handle your data, images, and device information.</p>
              <Link to="/privacy" className="btn btn-outline" style={{ marginTop: '8px' }}>
                Read Policy
              </Link>
            </div>

            <div className="card" style={{ textAlign: 'center' }}>
              <div className="card-icon" style={{ margin: '0 auto 16px' }}>
                <FileTextIcon size={22} style={{ color: '#8B5CF6' }} />
              </div>
              <h3 style={{ marginTop: 0 }}>Terms of Service</h3>
              <p>Subscription terms, usage limits, and acceptable use.</p>
              <Link to="/terms" className="btn btn-outline" style={{ marginTop: '8px' }}>
                Read Terms
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
